import { useState, useCallback, useEffect } from 'react';
import '@/App.css';
import AuthScreen from '@/components/AuthScreen';
import ForgotPassword from '@/components/ForgotPassword';
import OnboardingFlow from '@/components/OnboardingFlow';
import SplashScreen from '@/components/SplashScreen';
import HomePage from '@/components/HomePage';
import ExplanationView from '@/components/ExplanationView';
import ExplainInput from '@/components/ExplainInput';
import SavedTopics from '@/components/SavedTopics';
import BottomNav from '@/components/BottomNav';
import SocialCardPreview from '@/components/SocialCardPreview';
import SettingsPage from '@/components/SettingsPage';
import TermsPage from '@/components/TermsPage';
import AdminLogin from '@/components/AdminLogin';
import AdminPanel from '@/components/AdminPanel';
import InstallPrompt from '@/components/InstallPrompt';
import SharePage from '@/components/SharePage';
import DisclaimerScreen from '@/components/DisclaimerScreen';
import DailyDigest from '@/components/DailyDigest';
import WhatsAppPrompt from '@/components/WhatsAppPrompt';
import PulseDashboard from '@/components/PulseDashboard';
import MyPortfolioV2 from '@/components/MyPortfolioV2';
import HoldingsOnboarding from '@/components/HoldingsOnboarding';
import { AnimatePresence, motion } from 'framer-motion';
import { Settings, User, Home, Briefcase } from 'lucide-react';
import api from '@/api';
import { LOGO_URL } from '@/constants/categories';
import { ErrorBoundary } from '@/components/ErrorBoundary';

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState('splash'); // splash | auth | forgot | terms | onboarding | main | settings | admin-login | admin

  // Scroll to top on every view change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);
  const [activeTab, setActiveTab] = useState('home');
  const [showFeed, setShowFeed] = useState(false); // sub-state: when true on 'home' tab, shows full feed
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicsList, setTopicsList] = useState([]);
  const [socialCard, setSocialCard] = useState(null);

  // Scroll to top when switching tabs or toggling feed
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab, showFeed, selectedTopic]);
  const [showProfile, setShowProfile] = useState(false);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);

  // Show WhatsApp prompt after 5s on main view for users who haven't seen it
  useEffect(() => {
    if (view !== 'main' || !user) return;
    const alreadySeen = localStorage.getItem('wtf_wa_prompt_seen');
    if (alreadySeen) return;
    const timer = setTimeout(() => setShowWhatsAppPrompt(true), 5000);
    return () => clearTimeout(timer);
  }, [view, user]);

  // Force dark mode always
  useEffect(() => {
    document.body.classList.add('dark');
    localStorage.setItem('wtf_dark_mode', 'true');
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const stored = localStorage.getItem('wtf_user');
    const token = localStorage.getItem('wtf_token');
    const adminToken = localStorage.getItem('wtf_admin_token');
    const path = window.location.pathname;

    // Hidden admin route: /admin
    if (path === '/admin') {
      if (adminToken) {
        setView('admin');
      } else {
        setView('admin-login');
      }
      setAuthChecked(true);
      return;
    }

    // Terms page route: /terms
    if (path === '/terms') {
      setView('terms');
      setAuthChecked(true);
      return;
    }

    // Share target route: /share
    if (path === '/share') {
      setView('share');
      setAuthChecked(true);
      // Still load user if logged in
      if (stored && token) {
        try { setUser(JSON.parse(stored)); } catch {}
      }
      return;
    }

    // Daily digest route: /daily
    if (path === '/daily') {
      if (stored && token) {
        try {
          const userData = JSON.parse(stored);
          setUser(userData);
          setView('daily');
        } catch {
          setView('auth');
        }
      } else {
        setView('auth');
      }
      setAuthChecked(true);
      return;
    }

    // Deep link to a specific topic: /topic/TOPIC_ID
    const topicMatch = path.match(/^\/topic\/(.+)$/);
    if (topicMatch) {
      const topicId = topicMatch[1];
      if (stored && token) {
        try {
          const userData = JSON.parse(stored);
          setUser(userData);
          // Fetch the topic and open it
          api.getTrending().then(res => {
            const found = (res.topics || []).find(t => t.id === topicId);
            if (found) {
              setSelectedTopic(found);
              setTopicsList(res.topics || []);
            }
            setView('main');
          }).catch(() => setView('main'));
        } catch {
          setView('auth');
        }
      } else {
        setView('auth');
      }
      setAuthChecked(true);
      return;
    }

    // Check URL for payment callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const sessionId = params.get('session_id');
      if (sessionId) {
        handlePaymentSuccess(sessionId);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (adminToken && path === '/admin') {
      setView('admin');
      setAuthChecked(true);
      return;
    }

    if (stored && token) {
      try {
        const userData = JSON.parse(stored);
        setUser(userData);
        if (!userData.onboarding_complete) {
          setView('onboarding');
        } else if (!userData.advisory_consent_at) {
          // One-time advisory consent. Once ticked + accepted, user never sees it again.
          setView('disclaimer');
        } else {
          // Holdings-onboarding wall removed May 2026 (see getViewForUser).
          setView('main');
        }
      } catch {
        localStorage.removeItem('wtf_user');
        localStorage.removeItem('wtf_token');
      }
    }
    setAuthChecked(true);

    const handleExpiry = () => { setUser(null); setView('splash'); };
    window.addEventListener('auth_expired', handleExpiry);
    return () => window.removeEventListener('auth_expired', handleExpiry);
  }, []);

  const getViewForUser = (userData) => {
    if (!userData.onboarding_complete) return 'onboarding';
    if (!userData.advisory_consent_at) return 'disclaimer';
    // Holdings-onboarding wall removed May 2026 — users already gave their
    // preferences during the standard onboarding flow, so requiring a second
    // holdings picker after payment was redundant friction. The flow is still
    // available on-demand from the Portfolio editor.
    return 'main';
  };

  const handlePaymentSuccess = async (sessionId) => {
    try {
      const res = await api.checkPaymentStatus(sessionId);
      if (res.payment_status === 'paid') {
        // Fetch fresh user from /me so we pick up holdings_onboarding_complete=false
        // (set by Stripe webhook) and route the user into the holdings wall.
        // /api/auth/me returns {user: {...}} — unwrap before using, otherwise the
        // localStorage user gets clobbered to {user: {...}} and downstream
        // reads of user.preferences silently return undefined (May 2026 fix).
        let fresh = null;
        try {
          const meRes = await api.getMe();
          fresh = meRes?.user || meRes;
        } catch (e) { /* fall through */ }
        const stored = localStorage.getItem('wtf_user');
        const userData = fresh || (stored ? JSON.parse(stored) : null);
        if (userData) {
          userData.subscription_status = userData.subscription_status || 'active';
          userData.subscription_started = true;
          if (fresh) userData.holdings_onboarding_complete = fresh.holdings_onboarding_complete;
          else if (userData.holdings_onboarding_complete === undefined) userData.holdings_onboarding_complete = false;
          localStorage.setItem('wtf_user', JSON.stringify(userData));
          setUser(userData);
          setView(getViewForUser(userData));
          // Re-show WhatsApp prompt so subscriber can connect (after holdings wall).
          localStorage.removeItem('wtf_wa_prompt_seen');
          setTimeout(() => setShowWhatsAppPrompt(true), 1500);
        }
      }
    } catch (e) { console.error('Payment check failed:', e); }
  };

  const handleAuthSuccess = useCallback((userData) => {
    setUser(userData);
    const nextView = getViewForUser(userData);
    setView(nextView);
  }, []);

  const handleDeleteAccount = useCallback(() => {
    localStorage.removeItem('wtf_token');
    localStorage.removeItem('wtf_user');
    setUser(null);
    setView('splash');
  }, []);

  const handleOnboardingComplete = useCallback((updatedUser) => {
    setUser(updatedUser);
    // Advisory consent always comes right after onboarding for first-time users.
    setView(updatedUser.advisory_consent_at ? 'main' : 'disclaimer');
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('wtf_token');
    localStorage.removeItem('wtf_user');
    setUser(null);
    setView('splash');
    setShowProfile(false);
    setShowWhatsAppPrompt(false);
    setSelectedTopic(null);
    setActiveTab('home');
    setShowFeed(false);
  }, []);

  const handleAdminLogout = useCallback(() => {
    localStorage.removeItem('wtf_admin_token');
    window.history.pushState({}, '', '/');
    setView('splash');
  }, []);

  const handleUserUpdate = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('wtf_user', JSON.stringify(updatedUser));
  }, []);

  const handleTopicClick = useCallback((topic, allTopics) => {
    // Pulse highlights: navigate to Pulse tab instead of explanation view
    if (topic._type === 'trader' || topic._type === 'investor' || topic._type === 'news') {
      setShowFeed(false);
      setActiveTab('pulse');
      window.scrollTo(0, 0);
      return;
    }
    setSelectedTopic(topic);
    if (allTopics && allTopics.length > 0) setTopicsList(allTopics);
    window.scrollTo(0, 0);
  }, []);
  const handleBack = useCallback(() => { setSelectedTopic(null); window.scrollTo(0, 0); }, []);

  const handleNextTopic = useCallback(() => {
    if (!selectedTopic || topicsList.length === 0) return;
    const idx = topicsList.findIndex(t => t.id === selectedTopic.id);
    if (idx >= 0 && idx < topicsList.length - 1) {
      setSelectedTopic(topicsList[idx + 1]);
      window.scrollTo(0, 0);
    }
  }, [selectedTopic, topicsList]);

  const handlePrevTopic = useCallback(() => {
    if (!selectedTopic || topicsList.length === 0) return;
    const idx = topicsList.findIndex(t => t.id === selectedTopic.id);
    if (idx > 0) {
      setSelectedTopic(topicsList[idx - 1]);
      window.scrollTo(0, 0);
    }
  }, [selectedTopic, topicsList]);

  const handleShare = useCallback(async (topic) => {
    try {
      const data = await api.getRenderCard(topic.id);
      setSocialCard(data.card_data);
    } catch (e) { console.error('Failed to load social card:', e); }
  }, []);

  const handleExplained = useCallback((topic) => { setSelectedTopic(topic); }, []);

  const handleTabChange = useCallback((tab) => {
    setSelectedTopic(null);
    if (tab === 'portfolio') {
      // My Portfolio tab → dedicated view (mini-dashboard or setup)
      setView('portfolio');
      window.scrollTo(0, 0);
      return;
    }
    if (tab === 'pulse') {
      setShowFeed(false);
      setActiveTab('pulse');
    } else {
      setShowFeed(false);
      setActiveTab(tab);
    }
    window.scrollTo(0, 0);
  }, []);

  const handleBrowseAll = useCallback(() => {
    setShowFeed(true);
  }, []);

  // Loading
  if (!authChecked) {
    return (
      <div className="app-shell flex items-center justify-center min-h-screen">
        <img src={LOGO_URL} alt="WTFHappened" className="h-16 object-contain animate-pulse" />
      </div>
    );
  }

  // Terms page (standalone)
  if (view === 'terms') {
    return <div className="app-shell"><TermsPage onBack={() => setView('auth')} /></div>;
  }

  // Top bar icons — shown on all pages when user is logged in
  const TopBarIcons = (user && !selectedTopic) ? (
    <div className="fixed right-4 z-[60]" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
      <button data-testid="profile-btn" onClick={() => setShowProfile(!showProfile)}
        className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 shadow-sm flex items-center justify-center hover:bg-white/20 transition-all active:scale-95">
        <User size={20} className="text-white/70" />
      </button>
      <AnimatePresence>
        {showProfile && (
          <motion.div initial={{ opacity: 0, y: -5, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -5, scale: 0.95 }}
            className="absolute right-0 top-14 w-56 rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: '#fff', color: '#111' }}
            data-testid="profile-menu">
            <div className="px-4 py-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <p className="text-sm font-semibold truncate" style={{ color: '#111827' }}>{user.name}</p>
              <p className="text-xs truncate" style={{ color: '#6b7280' }}>{user.email || user.mobile}</p>
              <span className={`inline-block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase
                ${user.subscription_status === 'active' ? 'bg-green-100 text-green-700'
                  : user.subscription_status === 'trial' ? 'bg-blue-100 text-blue-700'
                  : 'bg-red-100 text-red-600'}`}>
                {user.subscription_status || 'trial'}
              </span>
            </div>
            <button data-testid="profile-home" onClick={() => { setShowProfile(false); setView('main'); setActiveTab('home'); setShowFeed(false); }}
              className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 transition-colors"
              style={{ color: '#1f2937' }}>
              <Home size={14} style={{ color: '#4b5563' }} /> Home
            </button>
            <button data-testid="profile-portfolio" onClick={() => { setShowProfile(false); setView('portfolio'); }}
              className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 transition-colors"
              style={{ color: '#1f2937' }}>
              <Briefcase size={14} style={{ color: '#10B981' }} /> My Portfolio
            </button>
            <button data-testid="profile-settings" onClick={() => { setShowProfile(false); setView('settings'); }}
              className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 transition-colors"
              style={{ color: '#1f2937' }}>
              <Settings size={14} style={{ color: '#4b5563' }} /> Settings
            </button>
            <button data-testid="logout-btn" onClick={handleLogout}
              className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 transition-colors"
              style={{ color: '#ef4444', borderTop: '1px solid #e5e7eb' }}>
              Sign Out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  ) : null;

  // Share target page (works with or without login)
  if (view === 'share') {
    return (
      <>
        {TopBarIcons}
        <SharePage onBack={() => { window.history.pushState({}, '', '/'); setView(user ? 'main' : 'splash'); }} />
      </>
    );
  }

  // Admin login
  if (view === 'admin-login') {
    return <div className="app-shell"><AdminLogin onBack={() => { window.history.pushState({}, '', '/'); setView('splash'); }} onAdminAuth={() => setView('admin')} /></div>;
  }

  // Admin panel
  if (view === 'admin') {
    return <AdminPanel onLogout={handleAdminLogout} />;
  }

  // Splash screen
  if (view === 'splash' && !user) {
    return (
      <>
        <AnimatePresence mode="wait">
          <SplashScreen onContinue={() => setView('auth')} />
        </AnimatePresence>
        <InstallPrompt />
      </>
    );
  }

  // Forgot password
  if (view === 'forgot') {
    return <div className="app-shell"><ForgotPassword onBack={() => setView('auth')} /></div>;
  }

  // Auth
  if (!user || view === 'auth') {
    return (
      <div className="app-shell">
        <AuthScreen
          onAuthSuccess={handleAuthSuccess}
          onForgotPassword={() => setView('forgot')}
        />
      </div>
    );
  }

  // Onboarding
  if (view === 'onboarding') {
    return <div className="app-shell">{TopBarIcons}<OnboardingFlow user={user} onComplete={handleOnboardingComplete} onLogout={handleLogout} /></div>;
  }

  // Disclaimer — shown every login/signup before main
  if (view === 'disclaimer') {
    return <DisclaimerScreen onAccept={async () => {
      try {
        const res = await api.recordAdvisoryConsent();
        if (res?.user) {
          localStorage.setItem('wtf_user', JSON.stringify(res.user));
          setUser(res.user);
        }
      } catch (e) {
        console.error('Failed to record advisory consent', e);
        // Even if API fails we still let the user proceed — they can't get
        // stuck behind the modal — but the server will prompt again next login.
      }
      // After consent, route to holdings wall if applicable, else main.
      const stored = localStorage.getItem('wtf_user');
      if (stored) {
        try {
          const u = JSON.parse(stored);
          setView(getViewForUser(u));
          return;
        } catch {}
      }
      setView('main');
    }} />;
  }

  // Holdings onboarding wall — mandatory after Stripe checkout (FB-PERS-1).
  if (view === 'holdings-onboarding') {
    return (
      <div className="app-shell">
        <HoldingsOnboarding
          user={user}
          onComplete={() => {
            const stored = localStorage.getItem('wtf_user');
            if (stored) {
              try {
                const u = JSON.parse(stored);
                u.holdings_onboarding_complete = true;
                localStorage.setItem('wtf_user', JSON.stringify(u));
                setUser(u);
              } catch {}
            }
            setView('main');
          }}
        />
      </div>
    );
  }

  // Edit Preferences (reuses OnboardingFlow in edit mode)
  if (view === 'edit-preferences') {
    return <div className="app-shell">{TopBarIcons}<OnboardingFlow user={user} editMode onComplete={async (prefs) => { await handleOnboardingComplete(prefs); setView('settings'); }} onLogout={() => setView('settings')} /></div>;
  }

  // Settings
  if (view === 'settings') {
    return (
      <div className="app-shell">
        {TopBarIcons}
        <SettingsPage
          user={user}
          onBack={() => setView('main')}
          onLogout={handleLogout}
          onUserUpdate={handleUserUpdate}
          onEditPreferences={() => setView('edit-preferences')}
        />
      </div>
    );
  }

  // Portfolio (premium feature — search-chip tracking UX, iter 61d)
  if (view === 'portfolio') {
    return (
      <div className="app-shell">
        {TopBarIcons}
        <MyPortfolioV2
          user={user}
          onBack={() => setView('main')}
          onTopicClick={(card) => {
            // Iter 64 — accept cards regardless of which id field they carry.
            // News topics → topic_id/id; investor entities → canonical_id;
            // reseller scored_products → product (no id); luxury entities →
            // canonical_id. handleTopicClick falls through to the explanation
            // service which keys off canonical_id OR title/name.
            const id = card?.topic_id || card?.canonical_id || card?.id;
            handleTopicClick({
              id,
              canonical_id: card?.canonical_id || id,
              title: card?.headline || card?.product || card?.entity_name || '',
              ...card,
            }, []);
          }}
        />
        {/* Footer nav stays available so the user can leave Portfolio at any time. */}
        <BottomNav
          activeTab="portfolio"
          onTabChange={(tab) => { if (tab !== 'portfolio') setView('main'); handleTabChange(tab); }}
        />
      </div>
    );
  }

  // Daily Digest
  if (view === 'daily') {
    return (
      <div className="app-shell">
        {TopBarIcons}
        <DailyDigest
          onTopicClick={handleTopicClick}
          onBack={() => { window.history.pushState({}, '', '/'); setView('main'); }}
        />
        {/* Topic detail modal (same component reused) */}
        <AnimatePresence>
          {selectedTopic && (
            <motion.div
              key="topic-modal-daily"
              data-testid="topic-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-[60] flex items-start justify-center"
              style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
              onClick={handleBack}
            >
              <motion.div
                initial={{ opacity: 0, y: 18, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 280, damping: 28 }}
                className="w-full max-w-md mx-auto overflow-y-auto"
                style={{ maxHeight: '100vh', background: '#0a0a0a' }}
                onClick={(e) => e.stopPropagation()}
              >
                <ExplanationView topic={selectedTopic} user={user} onBack={handleBack} onShare={handleShare} onNext={handleNextTopic} onPrev={handlePrevTopic} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Main app
  // When the user is on the Dashboard tab we strip .app-shell's 16-px
  // horizontal padding so PulseDashboard's cards can reach the screen edge
  // (May 2026 iter 62 — replaces the brittle `width:100vw` full-bleed trick).
  const shellModifier = activeTab === 'pulse' ? ' app-shell-flush' : '';
  return (
    <div className={`app-shell${shellModifier}`}>
      {TopBarIcons}

      <motion.div key={`${activeTab}-${showFeed}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
        {activeTab === 'home' && !showFeed && (
          <HomePage
            onBrowseAll={handleBrowseAll}
            onTopicClick={handleTopicClick}
            user={user}
            // After holdings onboarding, the hero CTA jumps the user to the
            // Dashboard tab (richer multi-section feed) — not the legacy
            // Portfolio settings screen. The Dashboard tab key is 'pulse' in
            // BottomNav (label="DASHBOARD"), so we route there explicitly.
            onOpenPortfolio={() => handleTabChange('pulse')}
          />
        )}
        {activeTab === 'home' && showFeed && (
          <PulseDashboard user={user} onTopicClick={handleTopicClick} onOpenPortfolio={() => setView('portfolio')} />
        )}
        {activeTab === 'pulse' && (
          <PulseDashboard user={user} onTopicClick={handleTopicClick} onOpenPortfolio={() => setView('portfolio')} />
        )}
        {activeTab === 'explain' && <ExplainInput onExplained={handleExplained} />}
        {activeTab === 'saved' && <SavedTopics onTopicClick={handleTopicClick} />}
      </motion.div>

      {/* Topic detail as a centred modal (not a full-page swap) */}
      <AnimatePresence>
        {selectedTopic && (
          <motion.div
            key="topic-modal"
            data-testid="topic-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[60] flex items-start justify-center"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
            onClick={handleBack}
          >
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="w-full max-w-md mx-auto overflow-y-auto"
              style={{ maxHeight: '100vh', background: '#0a0a0a' }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExplanationView topic={selectedTopic} user={user} onBack={handleBack} onShare={handleShare} onNext={handleNextTopic} onPrev={handlePrevTopic} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav activeTab={showFeed ? 'pulse' : activeTab} onTabChange={handleTabChange} />

      <AnimatePresence>
        {socialCard && <SocialCardPreview cardData={socialCard} onClose={() => setSocialCard(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {showWhatsAppPrompt && (
          <WhatsAppPrompt
            userName={user?.name}
            isSubscribed={user?.subscription_status === 'active'}
            onDismiss={() => {
              setShowWhatsAppPrompt(false);
              localStorage.setItem('wtf_wa_prompt_seen', 'true');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
